import React, { useEffect, useCallback } from 'react';

/**
 * DelightOverlay - The Invisible Net
 * 
 * An architectural layer that sits above all content as a transparent,
 * click-through canvas. This "net" can capture interactions and hang
 * beautiful, non-intrusive animations without interfering with user flow.
 * 
 * Philosophy:
 * - Universal detection: Monitors ALL clicks across the application
 * - Zero interference: pointer-events: none ensures clicks pass through
 * - Respectful: Honors accessibility preferences (prefers-reduced-motion)
 * - Extensible: Architecture supports future delightful animations
 * 
 * Current Implementation:
 * - Insect Burst: Random butterflies, ladybirds, grasshoppers on clicks
 */

const DelightOverlay = () => {
  /**
   * Insect Configuration
   * Each insect has unique characteristics for visual variety
   */
  const INSECTS = [
    { emoji: '🦋', size: 28, name: 'butterfly', weight: 0.8 },      // Light, graceful
    { emoji: '🐞', size: 18, name: 'ladybird', weight: 1.2 },       // Small, quick
    { emoji: '🦗', size: 24, name: 'grasshopper', weight: 1.0 },    // Medium, bouncy
  ];

  /**
   * Animation Parameters
   * Calibrated for "delightful puff, not imposing invasion"
   */
  const CONFIG = {
    triggerProbability: 1 / 7,        // 14.3% chance (1 in 7 clicks) - increased by 25% from 1/9
    delayBeforeAppear: 500,           // 0.5s delay
    animationDuration: 900,           // 0.9s animation
    totalLifecycle: 1400,             // 1.4s total (500 + 900)
    minInsects: 1,                    // Minimum per burst
    maxInsects: 2,                    // Maximum per burst
    spawnRadius: 40,                  // Random position variance (px)
    flightDistance: { min: 50, max: 150 }, // How far they travel
  };

  /**
   * Select Random Insect
   * Weighted random selection for variety
   */
  const getRandomInsect = useCallback(() => {
    return INSECTS[Math.floor(Math.random() * INSECTS.length)];
  }, []);

  /**
   * Generate Flight Path
   * Creates organic, varied movement patterns
   */
  const generateFlightPath = useCallback(() => {
    const patterns = [
      { x: 1, y: -1.5, rotate: 360, curve: 'up-right' },      // Arc up-right
      { x: -1, y: -1.5, rotate: -360, curve: 'up-left' },     // Arc up-left
      { x: 0.5, y: -2, rotate: 180, curve: 'flutter-up' },    // Flutter upward
      { x: -0.5, y: -2, rotate: -180, curve: 'flutter-up' },  // Flutter upward-left
    ];
    
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    const distance = CONFIG.flightDistance.min + 
                    Math.random() * (CONFIG.flightDistance.max - CONFIG.flightDistance.min);
    
    return {
      x: pattern.x * distance,
      y: pattern.y * distance,
      rotate: pattern.rotate + (Math.random() - 0.5) * 60, // Add wobble
    };
  }, []);

  /**
   * Create Insect Element
   * Generates a single animated insect at specified coordinates
   */
  const createInsect = useCallback((x, y) => {
    const insect = getRandomInsect();
    const flightPath = generateFlightPath();
    
    // Create element
    const element = document.createElement('div');
    element.className = 'delight-insect';
    element.setAttribute('aria-hidden', 'true'); // Hidden from screen readers
    element.textContent = insect.emoji;
    
    // Position with slight random variance (creates "burst" effect)
    const spawnX = x + (Math.random() - 0.5) * CONFIG.spawnRadius;
    const spawnY = y + (Math.random() - 0.5) * CONFIG.spawnRadius;
    
    // Apply styles
    element.style.cssText = `
      position: fixed;
      left: ${spawnX}px;
      top: ${spawnY}px;
      font-size: ${insect.size}px;
      pointer-events: none;
      z-index: 10000;
      user-select: none;
      will-change: transform, opacity;
      
      --flight-x: ${flightPath.x}px;
      --flight-y: ${flightPath.y}px;
      --flight-rotate: ${flightPath.rotate}deg;
      --animation-duration: ${CONFIG.animationDuration}ms;
    `;
    
    return element;
  }, [getRandomInsect, generateFlightPath]);

  /**
   * Create Insect Burst
   * Spawns 1-2 insects at click coordinates
   */
  const createInsectBurst = useCallback((x, y) => {
    // Determine number of insects (1 or 2, weighted 50/50)
    const count = Math.random() > 0.5 ? CONFIG.maxInsects : CONFIG.minInsects;
    
    for (let i = 0; i < count; i++) {
      const element = createInsect(x, y);
      document.body.appendChild(element);
      
      // Slight stagger for multiple insects
      const stagger = i * 50; // 50ms between each
      
      // Phase 1: Delay (0.5s)
      setTimeout(() => {
        element.classList.add('delight-insect-animate');
      }, CONFIG.delayBeforeAppear + stagger);
      
      // Phase 2: Cleanup (1.4s total)
      setTimeout(() => {
        element.remove();
      }, CONFIG.totalLifecycle + stagger);
    }
  }, [createInsect]);

  /**
   * Handle Click Events
   * Universal click detection with smart filtering
   */
  const handleClick = useCallback((e) => {
    // 1. Random probability check (1 in 7 clicks = 14.3%)
    // FIXED: Was inverted (>), now correct (<)
    if (Math.random() >= CONFIG.triggerProbability) return;
    
    // 2. Accessibility check (mandatory)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    
    // 3. Get click coordinates
    const x = e.clientX;
    const y = e.clientY;
    
    // 4. Create the burst
    createInsectBurst(x, y);
    
    // 5. Log for debugging
    console.log('🦋 Delight burst triggered at', { x, y });
  }, [createInsectBurst]);

  /**
   * Lifecycle: Attach/Detach Click Listener
   */
  useEffect(() => {
    // Attach universal click listener
    document.addEventListener('click', handleClick);
    
    console.log('🦋 DelightOverlay: Invisible net activated');
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('click', handleClick);
      console.log('🦋 DelightOverlay: Invisible net deactivated');
    };
  }, [handleClick]);

  /**
   * Render: Nothing visible
   * The "net" is conceptual - exists in event listeners and DOM injections
   */
  return null;
};

export default DelightOverlay;
